import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  PencilIcon,
  CheckIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  TagIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { Note } from '../../types/notes';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCustomers } from '../../hooks/useCustomers';
import SearchableSelect from '../common/SearchableSelect';

interface StickyNoteProps {
  note: Note;
  onUpdate: (id: string, updates: Partial<Note>) => void;
  onDelete: (id: string) => void;
  onPositionChange: (id: string, position: { x: number; y: number }) => void;
}

const StickyNote: React.FC<StickyNoteProps> = ({ note, onUpdate, onDelete, onPositionChange }) => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [tempPosition, setTempPosition] = useState<{ x: number; y: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [tempSize, setTempSize] = useState<{ width: number; height: number } | null>(null);
  const tempSizeRef = useRef<{ width: number; height: number } | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [newTag, setNewTag] = useState('');
  const noteRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Színek a note típusok alapján
  const colorSchemes = {
    yellow: 'bg-yellow-200 dark:bg-yellow-800 border-yellow-300 dark:border-yellow-700',
    blue: 'bg-blue-200 dark:bg-blue-800 border-blue-300 dark:border-blue-700',
    green: 'bg-green-200 dark:bg-green-800 border-green-300 dark:border-green-700',
    pink: 'bg-pink-200 dark:bg-pink-800 border-pink-300 dark:border-pink-700',
    purple: 'bg-purple-200 dark:bg-purple-800 border-purple-300 dark:border-purple-700',
    orange: 'bg-orange-200 dark:bg-orange-800 border-orange-300 dark:border-orange-700'
  };

  const textColors = {
    yellow: 'text-yellow-900 dark:text-yellow-100',
    blue: 'text-blue-900 dark:text-blue-100',
    green: 'text-green-900 dark:text-green-100',
    pink: 'text-pink-900 dark:text-pink-100',
    purple: 'text-purple-900 dark:text-purple-100',
    orange: 'text-orange-900 dark:text-orange-100'
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditing]);

  // Frissítjük az editContent-et, ha a note.content változik
  useEffect(() => {
    setEditContent(note.content);
  }, [note.content]);

  // Tag input focus kezelése
  useEffect(() => {
    if (showTagInput && tagInputRef.current) {
      tagInputRef.current.focus();
    }
  }, [showTagInput]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isEditing || isResizing) return;
    
    // Ha a resize handle-re kattintottak, ne indítsuk el a drag-et
    if ((e.target as HTMLElement).closest('[data-resize-handle]')) {
      return;
    }
    
    setIsDragging(true);
    setTempPosition(null); // Reset temp position
    setDragStart({
      x: e.clientX - note.position.x,
      y: e.clientY - note.position.y
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Csak a tempPosition-t frissítjük, nem a DB-t
    setTempPosition({ x: newX, y: newY });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    
    // Most mentjük a végső pozíciót a DB-be
    if (tempPosition) {
      onPositionChange(note.id, tempPosition);
      setTempPosition(null);
    }
  }, [tempPosition, note.id, onPositionChange]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSave = () => {
    onUpdate(note.id, { content: editContent });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(note.content);
    setIsEditing(false);
  };

  const handleColorChange = (color: keyof typeof colorSchemes) => {
    onUpdate(note.id, { color });
    setShowMenu(false);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !note.tags?.includes(newTag.trim())) {
      const updatedTags = [...(note.tags || []), newTag.trim()];
      onUpdate(note.id, { tags: updatedTags });
      setNewTag('');
      setShowTagInput(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = note.tags?.filter(tag => tag !== tagToRemove) || [];
    onUpdate(note.id, { tags: updatedTags });
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddTag();
    } else if (e.key === 'Escape') {
      setNewTag('');
      setShowTagInput(false);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    if (!customerId) return;
    
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    
    const customerName = customer.type === 'organization'
      ? customer.companyName
      : `${customer.lastName} ${customer.firstName}`;
    
    // Hozzáadjuk az új ügyfelet a meglévőkhöz
    const currentCustomers = note.customers || [];
    const currentCustomerIds = note.customerIds || [];
    
    // Ellenőrizzük, hogy már nincs-e hozzáadva
    if (currentCustomerIds.includes(customerId)) {
      setShowCustomerSelect(false);
      return;
    }
    
    onUpdate(note.id, { 
      customerIds: [...currentCustomerIds, customerId],
      customers: [...currentCustomers, { id: customerId, name: customerName }]
    });
    setShowCustomerSelect(false);
    setShowMenu(false); // Bezárjuk a menüt is
  };

  const handleRemoveCustomer = (customerId: string) => {
    const currentCustomers = note.customers || [];
    const currentCustomerIds = note.customerIds || [];
    
    onUpdate(note.id, { 
      customerIds: currentCustomerIds.filter(id => id !== customerId),
      customers: currentCustomers.filter(c => c.id !== customerId)
    });
  };

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    
    const newWidth = Math.max(200, resizeStart.width + deltaX); // Min 200px
    const newHeight = Math.max(150, resizeStart.height + deltaY); // Min 150px
    
    const newSize = { width: newWidth, height: newHeight };
    
    // Mindkét helyen frissítjük
    setTempSize(newSize);
    tempSizeRef.current = newSize;
  }, [isResizing, resizeStart]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    
    console.log('Resize end - saving size:', { 
      tempSize, 
      tempSizeRef: tempSizeRef.current, 
      noteId: note.id 
    });
    
    // A ref-ből vesszük a legfrissebb értéket
    const finalSize = tempSizeRef.current;
    if (finalSize) {
      onUpdate(note.id, { size: finalSize });
      setTempSize(null);
      tempSizeRef.current = null;
    }
  }, [note.id, onUpdate]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setTempSize(null); // Reset temp size
    tempSizeRef.current = null; // Reset ref is
    
    // Az aktuális méreteket használjuk, nem a DOM-ból
    const currentWidth = note.size?.width || 256;
    const currentHeight = note.size?.height || 256;
    
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: currentWidth,
      height: currentHeight
    });
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Drag közben a tempPosition-t használjuk, egyébként a note.position-t
  const noteX = tempPosition?.x ?? note.position.x;
  const noteY = tempPosition?.y ?? note.position.y;
  
  // Resize közben a tempSize-t használjuk, egyébként a note.size-t
  const noteWidth = tempSize?.width || note.size?.width || 256; // 256px = w-64
  const noteHeight = tempSize?.height || note.size?.height || 256; // 256px = h-64

  return (
    <div
      ref={noteRef}
      className={`absolute ${colorSchemes[note.color]} border-2 rounded-lg shadow-lg cursor-move select-none transform transition-transform hover:scale-105 ${isDragging || isResizing ? 'scale-105 z-50' : 'z-10'}`}
      style={{
        left: noteX,
        top: noteY,
        width: noteWidth,
        height: noteHeight,
        transform: isDragging ? 'rotate(-2deg)' : 'rotate(-1deg)'
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b border-current border-opacity-20">
        <div className="flex items-center space-x-1">
          {isEditing ? (
            <div className="flex space-x-1">
              <button
                onClick={handleSave}
                className="p-1 rounded hover:bg-black hover:bg-opacity-10"
              >
                <CheckIcon className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 rounded hover:bg-black hover:bg-opacity-10"
              >
                <XMarkIcon className="h-4 w-4 text-red-600" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 rounded hover:bg-black hover:bg-opacity-10"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          {/* Tag hozzáadás gomb */}
          <button
            onClick={() => setShowTagInput(!showTagInput)}
            className="p-1 rounded hover:bg-black hover:bg-opacity-10"
            title={t('notes.tags.add') || 'Tag hozzáadása'}
          >
            <TagIcon className="h-4 w-4" />
          </button>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded hover:bg-black hover:bg-opacity-10"
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
            
            {showMenu && (
              <div className="absolute left-full ml-1 top-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20 p-2 min-w-[200px]">
                <div className="flex space-x-1 mb-2">
                  {(Object.keys(colorSchemes) as Array<keyof typeof colorSchemes>).map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange(color)}
                      className={`w-6 h-6 rounded-full border-2 ${colorSchemes[color]} ${note.color === color ? 'ring-2 ring-gray-400' : ''}`}
                    />
                  ))}
                </div>
                
                {/* Ügyfél hozzárendelés */}
                <button
                  onClick={() => {
                    setShowCustomerSelect(!showCustomerSelect);
                  }}
                  className="flex items-center space-x-2 w-full px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-sm mb-1"
                >
                  <UserIcon className="h-4 w-4" />
                  <span>
                    {note.customerIds && note.customerIds.length > 0 
                      ? 'Ügyfél hozzáadása' 
                      : 'Ügyfél hozzárendelése'}
                  </span>
                </button>
                
                <button
                  onClick={() => {
                    onDelete(note.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center space-x-2 w-full px-2 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-sm"
                >
                  <TrashIcon className="h-4 w-4" />
                  <span>Törlés</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tag input */}
      {showTagInput && (
        <div className="px-3 pb-2">
          <input
            ref={tagInputRef}
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleTagKeyPress}
            onBlur={() => {
              if (!newTag.trim()) {
                setShowTagInput(false);
              }
            }}
            placeholder={t('notes.tags.placeholder') || 'Tag neve...'}
            className="w-full px-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
        </div>
      )}

      {/* Customer select */}
      {showCustomerSelect && (
        <div className="px-3 pb-2" onClick={(e) => e.stopPropagation()}>
          <SearchableSelect
            options={customers
              .filter(customer => !(note.customerIds || []).includes(customer.id))
              .map(customer => ({
                value: customer.id,
                label: customer.type === 'organization' 
                  ? customer.companyName 
                  : `${customer.lastName} ${customer.firstName}`
              }))}
            value=""
            onChange={handleCustomerChange}
            placeholder="Válassz ügyfelet..."
            searchPlaceholder="Keresés ügyfelek között..."
            clearable={false}
          />
        </div>
      )}

      {/* Ügyfelek megjelenítése */}
      {note.customers && note.customers.length > 0 && (
        <div className="px-3 pt-2 pb-1">
          <div className="flex flex-wrap gap-1">
            {note.customers.map(customer => (
              <div 
                key={customer.id}
                className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-800"
              >
                <UserIcon className="h-3 w-3 mr-1" />
                <span>{customer.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCustomer(customer.id);
                  }}
                  className="ml-1 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Ügyfél eltávolítása"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tag-ek megjelenítése */}
      {note.tags && note.tags.length > 0 && (
        <div className="px-3 pt-3 pb-2 flex flex-wrap gap-1">
          {note.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
            >
              {tag}
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:text-red-500"
                title={t('notes.tags.remove') || 'Tag eltávolítása'}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <div 
        className={`p-3 ${textColors[note.color]} overflow-hidden`}
        style={{
          height: `calc(100% - ${
            showTagInput 
              ? '120px' // Header + tag input + padding
              : showCustomerSelect
                ? '140px' // Header + customer select + padding
                : note.customers && note.customers.length > 0
                  ? (note.tags && note.tags.length > 0 ? '140px' : '120px') // Header + customers + tags?
                  : note.tags && note.tags.length > 0 
                    ? '100px' // Header + tags + padding
                    : '80px' // Header + padding
          })`
        }}
      >
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full h-full bg-transparent border-none outline-none resize-none text-sm leading-relaxed"
            placeholder={t('notes.placeholders.noteContent') || 'Írj ide valamit...'}
          />
        ) : (
          <div className="w-full h-full overflow-y-auto text-sm whitespace-pre-wrap break-words leading-relaxed">
            {note.content || (t('notes.placeholders.clickToEdit') || 'Kattints ide a szerkesztéshez...')}
          </div>
        )}
      </div>

      {/* Timestamp */}
      <div className={`absolute bottom-1 right-8 text-xs opacity-60 ${textColors[note.color]}`}>
        {new Date(note.updatedAt).toLocaleDateString('hu-HU')}
      </div>

      {/* Resize Handle */}
      <div
        data-resize-handle
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-50 hover:opacity-100 transition-opacity"
        onMouseDown={handleResizeStart}
        style={{
          background: `linear-gradient(-45deg, transparent 0%, transparent 30%, currentColor 30%, currentColor 40%, transparent 40%, transparent 60%, currentColor 60%, currentColor 70%, transparent 70%)`
        }}
        title="Méret változtatása"
      />
    </div>
  );
};

export default StickyNote;
